import React, { useState } from 'react';
import { useController } from "@rest-hooks/react";
import { useParams } from "react-router-dom";

import PageElementResource from "resources/organization/PageElementResource";

import { createFormData } from "./createFormData";
import { handleValidation } from "./handleValidation";

import { useError } from "utils/useErrorController";
import { updateDraggableList } from "utils/updateDraggableList";

export const usePageElementEditor = (elements, onSave, Block) => {
  const params = useParams();
  const organizationId = parseInt(params.organizationId);
  const { fetch } = useController();
  const { handleError, setLoading, validate } = useError();

  const [pageElements, setPageElements] = useState();
  const [pageElement, setPageElement] = useState(null);
  const [deletingPageElement, setDeletingPageElement] = useState(null);
  const [elementModal, setElementModal] = useState(false);

  const handleDeleteRequest = (id) => {
    setDeletingPageElement(id);
  };

  const handleDelete = async () => {
    try {
      await fetch(PageElementResource.delete(), {
        id: deletingPageElement,
        organization_id: organizationId,
      });
      handleSuccessfulResponse();
    } catch (error) {
      handleError(error);
    }
  };

  const handleDragEnd = async (result) => {
    try {
      updateDraggableList(
        pageElements,
        result.source.index,
        result.destination.index,
        setPageElements
      );
      await fetch(
        PageElementResource.update(),
        { organization_id: organizationId, id: result.draggableId },
        { position: result.destination.index }
      );
      onSave();
    } catch (error) {
      handleError(error);
    }
  };

  const handleUpdateRequest = (element) => {
    setPageElement(element);
    setElementModal(true);
  };

  const handleUpdate = async () => {
    try {
      validate(pageElement, handleValidation);
      const formData = createFormData(pageElement);
      await fetch(
        PageElementResource.update(),
        { organization_id: organizationId, id: pageElement.id },
        formData
      );
      handleSuccessfulResponse();
    } catch (error) {
      handleError(error);
    }
  };

  const handleCreate = async () => {
    try {
      validate(pageElement, handleValidation);
      const formData = createFormData(pageElement);
      await fetch(
        PageElementResource.create(),
        { organization_id: organizationId },
        formData
      );
      handleSuccessfulResponse();
    } catch (error) {
      handleError(error);
    }
  };

  const handleSuccessfulResponse = async () => {
    onSave();
    handleModalClose();
  };

  const handleModalClose = () => {
    setElementModal(false);
    setPageElement(null);
    setLoading(false);
    setDeletingPageElement(null);
  };

  const createElementState = (pageElements) => {
    return pageElements.map((pageElement) => ({
      id: pageElement.id,
      component: (
        <Block
          pageElement={pageElement}
          handleUpdateRequest={handleUpdateRequest}
          handleDeleteRequest={handleDeleteRequest}
        />
      ),
    }));
  };

  return {
    // State
    pageElements,
    setPageElements,
    pageElement,
    setPageElement,
    deletingPageElement,
    setDeletingPageElement,
    elementModal,
    setElementModal,
    
    // Handlers
    handleDelete,
    handleDragEnd,
    handleUpdate,
    handleCreate,
    handleModalClose,
    createElementState,
  };
};